// <copyright file="EsuReport.tsx">
// © 2025 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>
// Properties to initialize this UI component

import axios from "axios";
import { Chart as ChartJS, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { toZonedTime } from 'date-fns-tz';
import * as htmlToImage from 'html-to-image';
import moment from 'moment';
import { Button } from "primereact/button";
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from "primereact/multiselect";
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'chartjs-adapter-date-fns';
import 'moment-timezone';
import { RadioButton } from 'primereact/radiobutton';
import { SelectButton } from 'primereact/selectbutton';
import { Slider } from 'primereact/slider';
import { Toast } from "primereact/toast";
import React, { useState as _useState, useCallback, useEffect, useRef } from "react";
import styles from "src/components/EsuReport/EsuReport.module.css";
import { getCookieJsonField } from "src/utils/SharedUtils";
import { v4 as uuidv4 } from "uuid";
import type { ChartTypeRegistry, LegendItem, Plugin, TooltipItem } from 'chart.js';
import type { Moment } from 'moment';
import type { CalendarChangeParams } from 'primereact/calendar';
import type { SliderChangeParams } from 'primereact/slider';
import type { FC } from "react";

ChartJS.register(...registerables, annotationPlugin);

// Info/error message popup
const toast = React.createRef<Toast>();

// Polling interval in minutes
const POLLING_INTERVAL = 5;

interface EsuReportProps {
    useState?: typeof _useState; // optional hook override (for tests)
    apiBaseUrl: string;
    baseUrl: string;
}

interface FilterDataItem {
    DetectorRoadway: string;
    LinkDirection: string;
    MileMarker: string;
    CorridorName: string;
    CorridorRoadway: string;
}

type PlotItem = {
    Timestamp: string;
    Speed?: number;
    Volume?: number;
}

type EsuDataResponse = {
    ThreeMonth?: PlotItem[];
    OneYear?: PlotItem[];
    Primary?: PlotItem[];
    Latest?: PlotItem[];
}

type Dataset = {
    label: string;
    data: (number | undefined)[];
    type: string;
    borderColor?: string;
    backgroundColor?: string;
    barThickness : string | number;
    fill: boolean;
    yAxisID: string;
    xAxisID: string;
    pointRadius?: number;
    pointHoverRadius?: number;
    borderWidth?: number;
    order?: number;
    spanGaps: boolean;
};

type ChartData = {
    labels: string[];
    datasets: Dataset[];
};

// eslint-disable-next-line autofix/no-unused-vars
const timeStringToDate = (timeString: string): Date =>{
    if (timeString.length == 8 || timeString.length == 7){
        // Handle if it is just a time string, not datetime
        // e.g. Timestamp might still be being passed. i.e. "12:30:00" or "1:45:00"
        return new Date(`2000/01/01 ${timeString}`);
    } else {
        // Append a fixed date to the time string to create a full datetime string
        const date = new Date(`${timeString} GMT`);
        // You can get your output format and timezone
        const timeZone = 'America/New_York';

        // Convert the date to the desired time zone
        const zonedDate = toZonedTime(date, timeZone);
        return zonedDate
    }
}

/// Converts a string time into a Date object and into formatted Time string.
const timeStringToDateTimeObject = (timeString: string) => {
    const date = timeStringToDate(timeString);
    return date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
};

const EsuReport: FC<EsuReportProps> = (props: EsuReportProps) => {
    const DEFAULT_SELECTED_DATA_TYPE = null;
    const DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS = null;
    const DEFAULT_SELECTED_DATA_ROLLUP_PERIOD = null;
    const DEFAULT_SELECTED_ROADWAY = null;
    const DEFAULT_SELECTED_DIRECTION = null;
    const DEFAULT_SELECTED_CORRIDOR = null;
    const DEFAULT_SELECTED_CORRIDOR_SEGMENT = null;
    const DEFAULT_MILE_MARKER_RANGE: [number, number] = [0.0, 1000.0];
    const DEFAULT_PRIMARY_HISTORICAL_START_DATE = null;
    const DEFAULT_PRIMARY_HISTORICAL_END_DATE = null;
    const DEFAULT_SELECTED_DATA_TYPE_ALL = false;
    const DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS_ALL = false;
    const MIN_DATE = moment().subtract(1, 'year');
    const MAX_DATE = moment().subtract(1, 'day');

    const useState = props.useState ?? _useState;

    const [esuUpdateKey, setEsuUpdateKey] = useState<string>(uuidv4());

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date();
    const dayOfWeek = daysOfWeek[today.getDay()];
    const todayYear = today.getFullYear();
    const todayMonth = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const todayDay = String(today.getDate()).padStart(2, '0');

    const [latestDataYear, setLatestDataYear] = useState<number|null>(null);
    const [latestDataMonth, setLatestDataMonth] = useState<string>("");
    const [latestDataDay, setLatestDataDay] = useState<string>("");

    const [filterData, setFilterData] = useState<FilterDataItem[]>([]);

    // Data Type Selector
    const [selectedDataType, setSelectedDataType] = useState<string[] | null>(DEFAULT_SELECTED_DATA_TYPE);
    const [selectedDataTypeAll, setSelectedDataTypeAll] = useState<boolean>(DEFAULT_SELECTED_DATA_TYPE_ALL);
    const [dataTypes] = useState<{ label: string, value: string }[]>([
        { label: "Speeds", value: "speed" },
        { label: "Volumes", value: "volume" }
    ]);

    // Historical Data Options
    const [selectedHistoricalDataOptions, setSelectedHistoricalDataOptions] = useState<string[] | null>(DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS);
    const [selectedHistoricalDataOptionsAll, setSelectedHistoricalDataOptionsAll] = useState<boolean>(DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS_ALL);
    const [historicalDataOptions] = useState<{ label: string, value: string }[]>([
        { label: "Average Data 1-Year", value: "OneYear" },
        { label: "Average Data 3-Months", value: "ThreeMonth" }
    ]);

    // Data Rollup Period
    const [dataRollupPeriodOptions] = useState<{ label: string, value: string }[]>([
        { label: "15-minute Roll-ups", value: "15" },
        { label: "60-minute Roll-ups", value: "60" }
    ]);
    const [selectedDataRollupPeriod, setSelectedDataRollupPeriod] = useState<string | null>(DEFAULT_SELECTED_DATA_ROLLUP_PERIOD);

    // Date Range
    const [primaryHistoricalStartDate, setPrimaryHistoricalStartDate] = useState<Moment | null>(DEFAULT_PRIMARY_HISTORICAL_START_DATE);
    const [primaryHistoricalEndDate, setPrimaryHistoricalEndDate] = useState<Moment | null>(DEFAULT_PRIMARY_HISTORICAL_END_DATE);
    const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<string[]>([]);

    // Roadway
    const [roadwayOptions, setRoadwayOptions] = useState<string[]>([]);
    const [selectedRoadway, setSelectedRoadway] = useState<string | null>(DEFAULT_SELECTED_ROADWAY);

    // Direction
    const [directionOptions, setDirectionOptions] = useState<string[]>(["NB", "SB", "EB", "WB"]);
    const [selectedDirection, setSelectedDirection] = useState<string | null>(DEFAULT_SELECTED_DIRECTION);

    // Corridor
    const [corridorOptions, setCorridorOptions] = useState<string[]>([]);
    const [selectedCorridor, setSelectedCorridor] = useState<string | null>(DEFAULT_SELECTED_CORRIDOR);

    // Corridor Segments
    const [corridorSegmentOptions, setCorridorSegmentOptions] = useState<string[]>([]);
    const [selectedCorridorSegment, setSelectedCorridorSegment] = useState<string | null>(DEFAULT_SELECTED_CORRIDOR_SEGMENT);

    // Mile Marker
    const [mileMarkerRange, setMileMarkerRange] = useState<[number, number]>(DEFAULT_MILE_MARKER_RANGE);
    const [mileMarker, setMileMarker] = useState<[number, number]>(DEFAULT_MILE_MARKER_RANGE);

    // Plot Select Button
    const plotTypes = [
        { label: "Current vs Historical", value: 1 },
        { label: "Historical vs Historical", value: 2 },
    ];
    const [selectedPlot, setSelectedPlot] = useState<{ label: string, value: number }>(plotTypes[0]);
    const [selectedPlotButton, setSelectedPlotButton] = useState<number>(plotTypes[0].value);

    // Chart Data
    const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] } as ChartData);
    const [chartOptions, setChartOptions] = useState({});
    const [lastUpdated, setLastUpdated] = useState<string>("");
    const [historicalData, setHistoricalData] = useState<ChartData>({ labels: [], datasets: [] } as ChartData);
    const [currentData, setCurrentData] = useState<ChartData>({ labels: [], datasets: [] } as ChartData);

    // Automatic Refresh
    const [_pollCount, setPollCount] = useState<number>(0);
    const pollingInterval = useRef<NodeJS.Timeout | null>(null); // Use useRef for pollingInterval
    const [isPolling, setIsPolling] = useState<boolean>(true);
    const [canPlot, setCanPlot] = useState<boolean>(false);
    const [volumePlotType, setVolumePlotType] = useState<string>("line");
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentDay, setCurrentDay] = useState<Moment>(moment().tz('America/New_York'));

    const MAX_POLLS = 10;  // Define maximum number of polls

    /// Functionality for Draft filters:
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [originalSelectedDataType, setOriginalSelectedDataType] = useState<string[] | null>(DEFAULT_SELECTED_DATA_TYPE);
    const [originalSelectedHistoricalDataOptions, setOriginalSelectedHistoricalDataOptions] = useState<string[] | null>(DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS);
    const [originalSelectedDataRollupPeriod, setOriginalSelectedDataRollupPeriod] = useState<string | null>(DEFAULT_SELECTED_DATA_ROLLUP_PERIOD);
    const [originalPrimaryHistoricalStartDate, setOriginalPrimaryHistoricalStartDate] = useState<Moment | null>(DEFAULT_PRIMARY_HISTORICAL_START_DATE);
    const [originalPrimaryHistoricalEndDate, setOriginalPrimaryHistoricalEndDate] = useState<Moment | null>(DEFAULT_PRIMARY_HISTORICAL_END_DATE);
    const [originalSelectedRoadway, setOriginalSelectedRoadway] = useState<string | null>(DEFAULT_SELECTED_ROADWAY);
    const [originalSelectedDirection, setOriginalSelectedDirection] = useState<string | null>(DEFAULT_SELECTED_DIRECTION);
    const [originalSelectedCorridor, setOriginalSelectedCorridor] = useState<string | null>(DEFAULT_SELECTED_CORRIDOR);
    const [originalSelectedCorridorSegment, setOriginalSelectedCorridorSegment] = useState<string | null>(DEFAULT_SELECTED_CORRIDOR_SEGMENT);
    const [originalMileMarker, setOriginalMileMarker] = useState<[number, number]>(DEFAULT_MILE_MARKER_RANGE);
    const [originalMileMarkerRange, setOriginalMileMarkerRange] = useState<[number, number]>(DEFAULT_MILE_MARKER_RANGE);

    const fetchApplicableFilters = async () => {
        try {

            /// Actual Request Code
            const headers = {
                Authorization: `Bearer ${getCookieJsonField(
                    "DepSessionIdentity",
                    "token"
                )}`,
            };

            const response = await axios.get(`${props.apiBaseUrl}/applicable-filters`, { headers: headers });
            const filterData = response.data;
            const data = filterData as FilterDataItem[];

            // // Populate the initial data
            setFilterData(data);

            // // Populate options based on the initial data
            setOptions(data);
        } catch (error) {
            console.error('Error fetching CSV:', error);
            let errorMessage = 'An unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message || error.toString();
            } else if (axios.isAxiosError(error)) {
                // Axios error handling
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    errorMessage = error.response.data.message || error.response.statusText;
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'No response received from the server.';
                } else {
                    // Something happened in setting up the request that triggered an Error
                    errorMessage = error.message;
                }
            }

            toast.current?.show({
                severity: "error",
                summary: "Error fetching data",
                detail: errorMessage,
                sticky: true, // Show until dismissed by user
            });
        }
    };

    useEffect(() => {
        fetchApplicableFilters();
    }, []);

    const setOptions = useCallback((data: FilterDataItem[]) => {
        // Filter out invalid values and set the options
        const filteredData = data.filter(item =>
            (!selectedRoadway || item.DetectorRoadway === selectedRoadway) &&
            (!selectedDirection || item.LinkDirection === selectedDirection) &&
            (!selectedCorridor || item.CorridorRoadway === selectedCorridor) &&
            (!selectedCorridorSegment || item.CorridorName === selectedCorridorSegment)
        );

        const roadways = Array.from(new Set(filteredData.map(item => item.DetectorRoadway)));
        const directions = Array.from(new Set(filteredData.map(item => item.LinkDirection)));
        const corridors = Array.from(new Set(filteredData.map(item => item.CorridorRoadway)));
        const corridorSegments = Array.from(new Set(filteredData.map(item => item.CorridorName)));

        // Populate the options
        if (roadways) setRoadwayOptions(roadways.filter(item => item));
        if (directions) setDirectionOptions(directions.filter(item => item));
        if (corridors) setCorridorOptions(corridors.filter(item => item));
        if (corridorSegments) setCorridorSegmentOptions(corridorSegments.filter(item => item));
        adjustMileMarkerMinMax(filteredData);
    }, [selectedRoadway, selectedDirection, selectedCorridor, selectedCorridorSegment]);

    useEffect(() => {
        setOptions(filterData);
    }, [filterData, selectedRoadway, selectedDirection, selectedCorridor, selectedCorridorSegment, setOptions]);

    function adjustMileMarkerMinMax(data: FilterDataItem[])
    {
        const isRoadwaySelected = selectedRoadway != null;
        const isDirectionSelected = selectedDirection != null;
        const isEsuCorridorSelected = selectedCorridor != null;
        const isEsuCorridorSegmentSelected = selectedCorridorSegment != null;

        const isRoadwayChanged = selectedRoadway != originalSelectedRoadway;
        const isDirectionChanged = selectedDirection != originalSelectedDirection;
        const isEsuCorridorChanged = selectedCorridor != originalSelectedCorridor;
        const isEsuCorridorSegmentChanged = selectedCorridorSegment != originalSelectedCorridorSegment;
        const areAllFiltersOriginal = !(isRoadwayChanged || isDirectionChanged) && !(isEsuCorridorChanged || isEsuCorridorSegmentChanged);

        if ((isRoadwaySelected || isDirectionSelected || isEsuCorridorSelected || isEsuCorridorSegmentSelected) && areAllFiltersOriginal) // If anything is selected and all filters are equal to original, then this is a "revert" state.
        {
            setMileMarker(originalMileMarker);
            setMileMarkerRange(originalMileMarkerRange);
        }
        else {
            const mileMarkers = data.map(item => parseFloat(item.MileMarker)).map(value => (value === -Infinity || value === Infinity) || (Number.isNaN(value)) ? 0 : value).filter(value => Number.isFinite(value));
            const mileMarkerRange: [number, number] = [
            Math.min(...mileMarkers),
            Math.max(...mileMarkers)];
            setMileMarker(mileMarkerRange);
            setMileMarkerRange(mileMarkerRange);
        }
    }

    const customLegendPlugin: Plugin = {
        id: 'customLegend',
        afterUpdate(chart: ChartJS<keyof ChartTypeRegistry>) {
            const { legend } = chart;

            if (legend) {
                const speedItems: LegendItem[] = [];
                const volumeItems: LegendItem[] = [];

                // Separate legend items into Speed and Volume
                legend.legendItems?.forEach((item) => {
                    if (item.text.includes("Speed")) {
                        speedItems.push(item);
                    } else if (item.text.includes("Volume")) {
                        volumeItems.push(item);
                    }
                });

                // Combine into one array for display
                // Legend items will be displayed in two columns
                legend.legendItems = [...speedItems, ...volumeItems];
            }
        },
        afterDraw(chart: ChartJS<keyof ChartTypeRegistry>) {
            const { ctx, legend, chartArea } = chart;
            if (!legend) return;

            const padding = 10;
            const boxWidth = 10;
            const boxHeight = 10;

            ctx.save();

            const measureTextWidth = (text: string) => ctx.measureText(text).width;
            const speedTextWidths = legend.legendItems?.filter(item => item.text.includes("Speed")).map(item => measureTextWidth(item.text)) || [];
            const volumeTextWidths = legend.legendItems?.filter(item => item.text.includes("Volume")).map(item => measureTextWidth(item.text)) || [];
            const maxSpeedTextWidth = Math.max(...speedTextWidths, 0);
            const maxVolumeTextWidth = Math.max(...volumeTextWidths, 0);

            const columnWidth = (maxSpeedTextWidth + maxVolumeTextWidth + 2 * boxWidth + 4 * padding) / 2;

            // Calculate vertical spacing to place legend above the chart
            const yPosition = Math.max(chartArea.top - boxHeight - padding * 6, 0);
            const legendTop = yPosition - boxHeight - padding;
            const documentStyle = getComputedStyle(document.documentElement);
            ctx.fillStyle = documentStyle.getPropertyValue('--surface-ground');
            ctx.fillRect(0, legendTop, chart.width, boxHeight * (chart?.legend?.legendItems ? chart?.legend?.legendItems.length : 1));

            let speedYIndex = 0;
            let volumeYIndex = 0;

            // Draw speed items in left column and volume items in right column
            legend.legendItems?.forEach((item) => {
                let textXOffset = 0;
                let y = 0;

                if (item.text.includes("Speed")) {
                    textXOffset = (chart.width / 2) - columnWidth;
                    y = legendTop + padding + (speedYIndex * (boxHeight + padding));

                    speedYIndex += 1;
                } else if (item.text.includes("Volume")) {
                    textXOffset = (chart.width / 2) + padding * 2;
                    y = legendTop + padding + (volumeYIndex * (boxHeight + padding));

                    volumeYIndex += 1;
                }

                ctx.fillStyle = item.fillStyle || '#000';
                ctx.fillRect(textXOffset, y, boxWidth, boxHeight);

                ctx.strokeStyle = item.strokeStyle || '#000';
                ctx.strokeRect(textXOffset, y, boxWidth, boxHeight);

                ctx.textBaseline = 'middle';
                ctx.fillStyle = item.fontColor || '#000';
                ctx.fillText(item.text, textXOffset + boxWidth + padding, y + boxHeight / 2);
            });

            ctx.restore();
        }
    };

    ChartJS.register(customLegendPlugin);

    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColorSecondary = documentStyle.getPropertyValue('--text-color');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-hover');
        const isSpeedSelected = originalSelectedDataType != null ? originalSelectedDataType?.includes('speed') : false;
        const isVolumeSelected = originalSelectedDataType != null ? originalSelectedDataType?.includes('volume') : false;

        const scales = {
            x1: {
                type: 'time',
                offset: true,
                time: {
                    parser: 'h:mm a',
                    unit: 'hour',
                    displayFormats: {
                        hour: 'ha'
                    }
                },
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    major: {
                        enabled: true
                    },
                    color: textColorSecondary,
                    callback : function(value: string | number | Date, _index: number, _ticks: []) {
                        return moment(value).format('hA');
                    }
                },
                grid: {
                    color: surfaceBorder,
                    major: {
                        enabled: true
                    }
                },
                title: {
                    display: true,
                    text: "Time (ET)",
                    color: textColorSecondary,
                    font: {
                        family: 'Arial',
                        size: 20,
                        weight: 'bold',
                        lineHeight: 1.2
                    }
                }
            },
            x2: {
                stacked: true,
                offset: true,
                display: false,
                type: 'time',
                time: {
                    parser: 'h:mm a',
                    unit: 'hour',
                    displayFormats: {
                        hour: 'ha'
                    }
                },
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    major: {
                        enabled: true
                    },
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder,
                    major: {
                        enabled: true
                    }
                },
                title: {
                    display: false,
                    text: "Time2 (ET)",
                    color: textColorSecondary,
                    font: {
                        family: 'Arial',
                        size: 20,
                        weight: 'bold',
                        lineHeight: 1.2
                    }
                }
            },
            y: {
                type: 'linear',
                display: isSpeedSelected,
                position: 'left',
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    display: true,
                    color: surfaceBorder
                },
                title: {
                    display: isSpeedSelected,
                    text: "Speed (Mph)",
                    color: textColorSecondary,
                    font: {
                        family: 'Arial',
                        size: 20,
                        weight: 'bold',
                        lineHeight: 1.2
                    }
                },
                min: 0,
                grace: '10%'
            },
            y1: {
                type: 'linear',
                display: isVolumeSelected,
                position: isSpeedSelected || !isVolumeSelected ? 'right' : 'left',
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    display: true,
                    drawOnChartArea: false,
                    color: surfaceBorder
                },
                title: {
                    display: isVolumeSelected,
                    text: "Volume " + (originalSelectedDataRollupPeriod == "15" ? "(Vehicles/15m/Lane)" : "(Vehicles/60m/Lane)"),
                    color: textColorSecondary,
                    font: {
                        family: 'Arial',
                        size: 20,
                        weight: 'bold',
                        lineHeight: 1.2
                    }
                },
                min: 0,
                grace: '10%'
            }
        };
        const options = {
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            layout: {
                padding: {
                    top: 100, // Add padding to the top for the custom legend
                }
            },
            plugins: {
                legend: {
                    display: false, // Hide default legend to use custom plugin
                },
                customLegend: true,
                tooltip : {
                    callbacks: {
                        title: function(context : [TooltipItem<'line' | 'bar'>]) {
                            const dateParsed = moment(context[0].label, "MMM D YYYY, h:m:ss a");
                            return dateParsed.format("h:mm A");
                        }
                    }
                },
                annotation: {
                    annotations: {
                        speedLine: {
                            type: 'line',
                            scaleID: 'y',
                            value: 45,
                            borderColor: 'blue',
                            order: 4,
                            borderWidth: 1,
                            display: isSpeedSelected && !isVolumeSelected,
                            label: {
                                content: 'Speed: 45',
                                enabled: true,
                                display: true,
                                backgroundColor: 'rgba(128, 128, 128, 0.5)', // Gray with 50% transparency,
                                order: 4,
                                position: 'end',
                            }
                        },
                        volumeLine: {
                            type: 'line',
                            scaleID: 'y1',
                            value: (originalSelectedDataRollupPeriod == "15" ? 300 : 1200),
                            borderColor: 'red',
                            borderWidth: 1,
                            display: !isSpeedSelected && isVolumeSelected,
                            label: {
                                content: (originalSelectedDataRollupPeriod == "15" ? 'Volume: 300' : 'Volume: 1200'),
                                display: true,
                                enabled: true,
                                position: 'end',
                                backgroundColor: 'rgba(128, 128, 128, 0.5)', // Gray with 50% transparency,
                                order: 4,
                            },
                            order: 4,
                        }
                    }
                }
            },
            scales: scales
        };

        setChartOptions(options);
    }, [originalSelectedDataType, originalSelectedDataRollupPeriod]);

    const applyFilters = async (plot = true) => {
        // Apply the filters
        setOriginalSelectedDataType(selectedDataType);
        setOriginalSelectedHistoricalDataOptions(selectedHistoricalDataOptions);
        setOriginalSelectedDataRollupPeriod(selectedDataRollupPeriod);
        setOriginalPrimaryHistoricalStartDate(primaryHistoricalStartDate);
        setOriginalPrimaryHistoricalEndDate(primaryHistoricalEndDate);
        setOriginalSelectedRoadway(selectedRoadway);
        setOriginalSelectedDirection(selectedDirection);
        setOriginalSelectedCorridor(selectedCorridor);
        setOriginalSelectedCorridorSegment(selectedCorridorSegment);
        setOriginalMileMarker(mileMarker);
        setOriginalMileMarkerRange(mileMarkerRange);
        if (plot) {
            setFiltersApplied(true);
        }
    };

    const revertFilters = () => {
        setSelectedDataType(originalSelectedDataType);
        setSelectedHistoricalDataOptions(originalSelectedHistoricalDataOptions);
        setSelectedDataRollupPeriod(originalSelectedDataRollupPeriod);
        setPrimaryHistoricalStartDate(originalPrimaryHistoricalStartDate);
        setPrimaryHistoricalEndDate(originalPrimaryHistoricalEndDate);
        setSelectedRoadway(originalSelectedRoadway);
        setSelectedDirection(originalSelectedDirection);
        setSelectedCorridor(originalSelectedCorridor);
        setSelectedCorridorSegment(originalSelectedCorridorSegment);
        setMileMarkerRange(originalMileMarkerRange);
        setMileMarker(originalMileMarker);
    };

    const hasPendingChanges = () => {
        const conditions = [
            {
                condition: selectedDataType !== originalSelectedDataType && selectedDataType !== DEFAULT_SELECTED_DATA_TYPE,
                values: `selectedDataType: ${selectedDataType}, originalSelectedDataType: ${originalSelectedDataType}, DEFAULT_SELECTED_DATA_TYPE: ${DEFAULT_SELECTED_DATA_TYPE}`
            },
            {
                condition: selectedHistoricalDataOptions !== originalSelectedHistoricalDataOptions && selectedHistoricalDataOptions !== DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS,
                values: `selectedHistoricalDataOptions: ${selectedHistoricalDataOptions}, originalSelectedHistoricalDataOptions: ${originalSelectedHistoricalDataOptions}, DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS: ${DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS}`
            },
            {
                condition: selectedDataRollupPeriod !== originalSelectedDataRollupPeriod && selectedDataRollupPeriod !== DEFAULT_SELECTED_DATA_ROLLUP_PERIOD,
                values: `selectedDataRollupPeriod: ${selectedDataRollupPeriod}, originalSelectedDataRollupPeriod: ${originalSelectedDataRollupPeriod}, DEFAULT_SELECTED_DATA_ROLLUP_PERIOD: ${DEFAULT_SELECTED_DATA_ROLLUP_PERIOD}`
            },
            {
                condition: primaryHistoricalStartDate !== originalPrimaryHistoricalStartDate && primaryHistoricalStartDate !== DEFAULT_PRIMARY_HISTORICAL_START_DATE,
                values: `primaryHistoricalStartDate: ${primaryHistoricalStartDate}, originalPrimaryHistoricalStartDate: ${originalPrimaryHistoricalStartDate}, DEFAULT_PRIMARY_HISTORICAL_START_DATE: ${DEFAULT_PRIMARY_HISTORICAL_START_DATE}`
            },
            {
                condition: primaryHistoricalEndDate !== originalPrimaryHistoricalEndDate && primaryHistoricalEndDate !== DEFAULT_PRIMARY_HISTORICAL_END_DATE,
                values: `primaryHistoricalEndDate: ${primaryHistoricalEndDate}, originalPrimaryHistoricalEndDate: ${originalPrimaryHistoricalEndDate}, DEFAULT_PRIMARY_HISTORICAL_END_DATE: ${DEFAULT_PRIMARY_HISTORICAL_END_DATE}`
            },
            {
                condition: selectedRoadway !== originalSelectedRoadway && selectedRoadway !== DEFAULT_SELECTED_ROADWAY,
                values: `selectedRoadway: ${selectedRoadway}, originalSelectedRoadway: ${originalSelectedRoadway}, DEFAULT_SELECTED_ROADWAY: ${DEFAULT_SELECTED_ROADWAY}`
            },
            {
                condition: selectedDirection !== originalSelectedDirection && selectedDirection !== DEFAULT_SELECTED_DIRECTION,
                values: `selectedDirection: ${selectedDirection}, originalSelectedDirection: ${originalSelectedDirection}, DEFAULT_SELECTED_DIRECTION: ${DEFAULT_SELECTED_DIRECTION}`
            },
            {
                condition: selectedCorridor !== originalSelectedCorridor && selectedCorridor !== DEFAULT_SELECTED_CORRIDOR,
                values: `selectedCorridor: ${selectedCorridor}, originalSelectedCorridor: ${originalSelectedCorridor}, DEFAULT_SELECTED_CORRIDOR: ${DEFAULT_SELECTED_CORRIDOR}`
            },
            {
                condition: selectedCorridorSegment !== originalSelectedCorridorSegment && selectedCorridorSegment !== DEFAULT_SELECTED_CORRIDOR_SEGMENT,
                values: `selectedCorridorSegment: ${selectedCorridorSegment}, originalSelectedCorridorSegment: ${originalSelectedCorridorSegment}, DEFAULT_SELECTED_CORRIDOR_SEGMENT: ${DEFAULT_SELECTED_CORRIDOR_SEGMENT}`
            },
            {
                condition: JSON.stringify(mileMarker) !== JSON.stringify(originalMileMarker) && JSON.stringify(mileMarker) !== JSON.stringify(mileMarkerRange),
                values: `mileMarker: ${JSON.stringify(mileMarker)}, originalMileMarker: ${JSON.stringify(originalMileMarker)}, DEFAULT_MILE_MARKER_RANGE: ${JSON.stringify(mileMarkerRange)}`
            }
        ];

        // Check if any of the conditions are true
        return conditions.some(cond => cond.condition);
    };

    const checkCanPlot = useCallback(() => new Promise((resolve) => {
        const canPlot = originalSelectedDataType && originalSelectedDataType.length > 0 &&
            originalSelectedHistoricalDataOptions && originalSelectedHistoricalDataOptions.length > 0 &&
            originalSelectedDataRollupPeriod && originalSelectedRoadway &&
            (selectedPlot.value !== 2 || (originalPrimaryHistoricalStartDate && originalPrimaryHistoricalEndDate));
        setCanPlot(!!canPlot);
        if (!canPlot) {
            stopPolling();
        }
        resolve(!!canPlot);
    }), [
        originalSelectedDataType,
        originalSelectedHistoricalDataOptions,
        originalSelectedDataRollupPeriod,
        originalSelectedRoadway,
        originalSelectedDirection,
        originalSelectedCorridor,
        originalSelectedCorridorSegment,
        originalMileMarker,
        selectedPlot,
        originalPrimaryHistoricalStartDate,
        originalPrimaryHistoricalEndDate,
    ]);

    useEffect(() => {
        checkCanPlot();
    }, [
        originalSelectedDataType,
        originalSelectedHistoricalDataOptions,
        originalSelectedDataRollupPeriod,
        originalSelectedRoadway,
        originalSelectedDirection,
        originalSelectedCorridor,
        originalSelectedCorridorSegment,
        originalMileMarker,
        selectedPlot,
        originalPrimaryHistoricalStartDate,
        originalPrimaryHistoricalEndDate,
        checkCanPlot
    ]);

    // Clear selected filters
    async function clearFilters() {
        setSelectedDataType(DEFAULT_SELECTED_DATA_TYPE);
        setSelectedHistoricalDataOptions(DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS);
        setSelectedDataRollupPeriod(DEFAULT_SELECTED_DATA_ROLLUP_PERIOD);
        setSelectedRoadway(DEFAULT_SELECTED_ROADWAY);
        setSelectedDirection(DEFAULT_SELECTED_DIRECTION);
        setSelectedCorridor(DEFAULT_SELECTED_CORRIDOR);
        setSelectedCorridorSegment(DEFAULT_SELECTED_CORRIDOR_SEGMENT);
        setSelectedDataTypeAll(DEFAULT_SELECTED_DATA_TYPE_ALL);
        setSelectedHistoricalDataOptionsAll(DEFAULT_SELECTED_HISTORICAL_DATA_OPTIONS_ALL);
        setMileMarkerRange(DEFAULT_MILE_MARKER_RANGE);
        setMileMarker(DEFAULT_MILE_MARKER_RANGE);
        setPrimaryHistoricalEndDate(DEFAULT_PRIMARY_HISTORICAL_END_DATE);
        setPrimaryHistoricalStartDate(DEFAULT_PRIMARY_HISTORICAL_START_DATE);
    }

    useEffect(() => {
        applyAndUpdateFilters();
    }, [filtersCleared,
        originalSelectedDataType,
        originalSelectedHistoricalDataOptions,
        originalSelectedDataRollupPeriod,
        originalSelectedRoadway,
        originalSelectedDirection,
        originalSelectedCorridor,
        originalSelectedCorridorSegment,
        originalMileMarker,
        selectedPlot,
        originalPrimaryHistoricalStartDate,
        originalPrimaryHistoricalEndDate,
        checkCanPlot
    ]);

    const applyAndUpdateFilters = () => {
        if (filtersCleared) {
            setOptions(filterData);
            setFiltersCleared(false);
        }
    };

    useEffect(() => {
        setFiltersCleared(true);
        setEsuUpdateKey(uuidv4());  // Update the key to ensure the component re-renders
        setLastUpdated("");
        setChartData({ labels: [], datasets: [] } as ChartData);  // Clear existing plot data
    }, [
        selectedPlot,
    ]);

    useEffect(() => {
        if (filtersApplied) {
            executePlot();
            setFiltersApplied(false); // Reset the flag
        }
    }, [
        filtersApplied,
        originalSelectedDataType,
        originalSelectedHistoricalDataOptions,
        originalSelectedDataRollupPeriod,
        originalSelectedRoadway,
        originalPrimaryHistoricalStartDate,
        originalPrimaryHistoricalEndDate,
    ]);

    const executePlot = async () => {
        const result = await checkCanPlot();
        if (result) {
            stopPolling();
            setIsLoading(true);
            await Promise.all([fetchPlotData(), fetchLatestData()]);
            setIsLoading(false);
        }
        else {
            const errors: string[] = [];
            if (!selectedDataType || selectedDataType.length === 0) errors.push("Data Type is required.");
            if (!selectedHistoricalDataOptions || selectedHistoricalDataOptions.length === 0) errors.push("Historical Data Option is required.");
            if (!selectedDataRollupPeriod) errors.push("Data Rollup Period is required.");
            if (!selectedRoadway) errors.push("Roadway is required.");
            if (errors.length > 0) {
                toast.current?.show({
                    severity: "error",
                    summary: "Required filters not set",
                    detail: errors.join(", "),
                    sticky: true, // Show until dismissed by user
                });
            }
        }
    };

    useEffect(() => {
        // Update chart types based on volumePlotType change
        const updatedChartData = { ...chartData };
        const existingDatasets = updatedChartData.datasets || [];

        // Update the type of datasets containing "Volume"
        updatedChartData.datasets = existingDatasets.map((dataset: Dataset) => {
            if (dataset.label && dataset.label.includes("Volume")) {
                return {
                    ...dataset,
                    type: volumePlotType
                };
            }
            return dataset;
        });

        // Set the updated chart data
        setChartData(updatedChartData);
    }, [volumePlotType]);

    const getColorByLabel = (label: string): string => {
        // obtained from https://colorbrewer2.org/#type=qualitative&scheme=Accent&n=6
        if (label.includes('3-Months') && label.includes('Speed')) return '#7fc97f';  // Green
        if (label.includes('3-Months') && label.includes('Volume')) return '#573f77';  // Purple
        if (label.includes('1-Year') && label.includes('Speed')) return '#fdc086';  // Orange
        if (label.includes('1-Year') && label.includes('Volume')) return '#cc0';  // Yellow
        if (label.includes('Current') && label.includes('Speed')) return '#386cb0';  // Blue
        if (label.includes('Current') && label.includes('Volume')) return '#f0027f';  // Pink/Magenta
        if (label.includes('Primary') && label.includes('Speed')) return '#386cb0';  // Blue
        if (label.includes('Primary') && label.includes('Volume')) return '#f0027f';  // Pink/Magenta
        return '#000000';  // Default to black if no matches
    };

    const processAndSetChartData = async() => {
        try {

            console.debug(`Historical Data: ${historicalData.datasets}`);
            console.debug(`Current Data: ${currentData.datasets}`);

            historicalData.datasets.forEach(dataset => dataset.data = dataset.data.filter(x => x !== undefined));

            const baseLabelDataset = originalSelectedDataRollupPeriod === "15" ? generateTimeRangeDay(15) : generateTimeRangeDay(60);
            const latestTimeIndex = currentData.labels.findIndex(data => !baseLabelDataset.includes(data));

            const labels = Array.from(new Set([...historicalData.labels, ...currentData.labels])); // This effectively inserts the latest data's label into the x-axis set for plotting
            labels.sort((a, b) => { // Ensure the latest point is inserted correctly
                const dateA = new Date(`2000/01/01 ${a}`);
                const dateB = new Date(`2000/01/01 ${b}`);

                return dateA.getTime() - dateB.getTime();
            });

            if (latestTimeIndex !== -1) { // This will be -1 in the case the latest data point coincides with a 15 minute interval boundry (0, 15, 30, 45)
                const latestTimeValue = currentData.labels[latestTimeIndex];
                const latestTimeCombinedIndex = labels.findIndex(label => label === latestTimeValue);

                if (selectedPlot.value === 1) {
                    historicalData.datasets.forEach(dataset => {
                        dataset.data.splice(latestTimeCombinedIndex, 0, undefined); // Add an undefined point into the historical dataset that corresponds to the latest point. This ensures the plotting indecides match up to the merged labels.
                    });
                }
            }

            let combinedDatasets = [...historicalData.datasets, ...currentData.datasets];

            // Update the type of datasets containing "Volume"
            combinedDatasets = combinedDatasets.map((dataset: Dataset) => {
                if (dataset.label && dataset.label.includes("Volume")) {
                    return {
                        ...dataset,
                        type: volumePlotType
                    };
                }
                return dataset;
            });

            setChartData({ labels, datasets: combinedDatasets });
        }
        catch (error) {
            console.error("Error plotting data", error);
            let errorMessage = 'An unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message || error.toString();
            } else if (axios.isAxiosError(error)) {
                // Axios error handling
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    errorMessage = error.response.data.message || error.response.statusText;
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'No response received from the server.';
                } else {
                    // Something happened in setting up the request that triggered an Error
                    errorMessage = error.message;
                }
            }

            toast.current?.show({
                severity: "error",
                summary: "Error plotting data",
                detail: errorMessage,
                sticky: true, // Show until dismissed by user
            });
        }
    }

    useEffect(() => {
        setTimeout(processAndSetChartData, 0);
    }, [historicalData, currentData])

    const fetchPlotData = async () => {
        try {

            const headers = {
                Authorization: `Bearer ${getCookieJsonField(
                    "DepSessionIdentity",
                    "token"
                )}`,
            };

            const params = {
                interval_min: originalSelectedDataRollupPeriod,
                display_choice: originalSelectedDataType?.join(","),
                corridor_name: originalSelectedRoadway,
                direction: originalSelectedDirection,
                esu_corridor_name: originalSelectedCorridor,
                corridor_segment: originalSelectedCorridorSegment,
                "mile_marker-range": originalMileMarker.join(","),
                reference_data: originalSelectedHistoricalDataOptions?.join(","),
                primary_history_start: originalPrimaryHistoricalStartDate?.toISOString(), // Use these for the HvH view
                primary_history_end: originalPrimaryHistoricalEndDate?.toISOString(),
            };

            const endpointList = [];
            let oneYearFlag = false;
            let threeMonthFlag = false;
            let hvhFlag = false;
            switch (selectedPlot.value) {
                case 1: // Current vs Historical View
                    if (originalSelectedHistoricalDataOptions?.includes("OneYear")) {
                        endpointList.push("historical-1year");
                        oneYearFlag = true;
                    }
                    if ((originalSelectedHistoricalDataOptions?.includes("ThreeMonth"))) {
                        endpointList.push("historical-3month");
                        threeMonthFlag = true;
                    }
                    break;
                case 2:
                    endpointList.push("historical-v-historical-primary");
                    hvhFlag = true;
                    if (originalSelectedHistoricalDataOptions?.includes("OneYear")) {
                        endpointList.push("historical-v-historical-1year");
                        oneYearFlag = true;
                    }
                    if ((originalSelectedHistoricalDataOptions?.includes("ThreeMonth"))) {
                        endpointList.push("historical-v-historical-3month");
                        threeMonthFlag = true;
                    }
                    break;
                default:
                    return;
            }

            /// Real Api Calls
            const threeMonthDataset: PlotItem[] = [];
            const oneYearDataset: PlotItem[] = [];
            const primaryDataset: PlotItem[] = [];

            const promises = endpointList.map(endpoint => axios.get<EsuDataResponse>(`${props.apiBaseUrl}/${endpoint}`, { params, headers }));
            const responses = await Promise.all(promises);
            const data = responses.map(response => response.data);
            for (const item of data) {
                if (threeMonthFlag && item.ThreeMonth != null) {
                    threeMonthDataset.push(...item.ThreeMonth);
                }
                if (oneYearFlag && item.OneYear != null) {
                    oneYearDataset.push(...item.OneYear);
                }
                if (hvhFlag && item.Primary != null) {
                    primaryDataset.push(...item.Primary);
                }
            }

            const labels: string[] = originalSelectedDataRollupPeriod === "15" ? generateTimeRangeDay(15) : generateTimeRangeDay(60);
            const speedDatasets = [];
            const volumeDatasets = [];

            if (threeMonthFlag) {
                if (selectedDataType?.includes("speed")) {
                    const label = "Average Speed 3-Months"
                    const dataset3moSpeed: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: "line",
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        order: 2,
                        spanGaps: true,
                        barThickness: 8
                    };
                    threeMonthDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Speed){
                            dataset3moSpeed.data[insertionIndex] = item.Speed;
                        }
                    });
                    speedDatasets.push(dataset3moSpeed);
                }

                if (selectedDataType?.includes("volume")) {
                    const label = "Average Volume 3-Months"
                    const dataset3moVolume: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: volumePlotType,
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y1',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        order: 2,
                        spanGaps: true,
                        barThickness: originalSelectedDataRollupPeriod === "15" ? 4 : 16
                    };
                    threeMonthDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Volume){
                            dataset3moVolume.data[insertionIndex] = item.Volume;
                        }
                    });
                    volumeDatasets.push(dataset3moVolume);
                }
            }

            if (oneYearFlag) {
                if (selectedDataType?.includes("speed")) {
                    const label = "Average Speed 1-Year"
                    const dataset1yrSpeed: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: "line",
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        order: 3,
                        spanGaps: true,
                        barThickness: 8
                    };
                    oneYearDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Speed){
                            dataset1yrSpeed.data[insertionIndex] = item.Speed;
                        }
                    });
                    speedDatasets.push(dataset1yrSpeed);
                }

                if (selectedDataType?.includes("volume")) {
                    const label = "Average Volume 1-Year"
                    const dataset1yrVolume: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: volumePlotType,
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y1',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        order: 3,
                        spanGaps: true,
                        barThickness: originalSelectedDataRollupPeriod === "15" ? 4 : 16
                    };
                    oneYearDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Volume){
                            dataset1yrVolume.data[insertionIndex] = item.Volume;
                        }
                    });
                    volumeDatasets.push(dataset1yrVolume);
                }
            }

            if (hvhFlag) {
                if (selectedDataType?.includes("speed")) {
                    const label = "Average Speed Primary"
                    const datasetPrimarySpeed: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: "line",
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        spanGaps: true,
                        barThickness: 8
                    };
                    primaryDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Speed){
                            datasetPrimarySpeed.data[insertionIndex] = item.Speed;
                        }
                    });
                    speedDatasets.push(datasetPrimarySpeed);
                }

                if (selectedDataType?.includes("volume")) {
                    const label = "Average Volume Primary"
                    const datasetPrimaryVolume: Dataset = {
                        label: label,
                        data: Array(labels.length).fill(null),
                        type: volumePlotType,
                        borderColor: getColorByLabel(label),
                        backgroundColor: getColorByLabel(label),
                        fill: false,
                        yAxisID: 'y1',
                        xAxisID: 'x1',
                        borderWidth: 1,
                        spanGaps: true,
                        barThickness: originalSelectedDataRollupPeriod === "15" ? 4 : 16
                    };
                    primaryDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Volume){
                            datasetPrimaryVolume.data[insertionIndex] = item.Volume;
                        }
                    });
                    volumeDatasets.push(datasetPrimaryVolume);
                }
            }

            const combinedDatasets = [...speedDatasets, ...volumeDatasets];

            setHistoricalData({ labels, datasets: combinedDatasets });
        } catch (error) {
            console.error("Error fetching plot data", error);
            let errorMessage = 'An unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message || error.toString();
            } else if (axios.isAxiosError(error)) {
                // Axios error handling
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    errorMessage = error.response.data.message || error.response.statusText;
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'No response received from the server.';
                } else {
                    // Something happened in setting up the request that triggered an Error
                    errorMessage = error.message;
                }
            }

            toast.current?.show({
                severity: "error",
                summary: "Error fetching plot data",
                detail: errorMessage,
                sticky: true, // Show until dismissed by user
            });
        }
    };

    const fetchLatestData = async () => {
        try {
            setPollCount((prevCount) => {
                if (prevCount >= MAX_POLLS) {
                    stopPolling();
                    return prevCount;
                } else {
                    return prevCount + 1;
                }
            });

            if (selectedPlot.value !== 1) // Check to ensure that the HvH plot is not being plotted.
            {
                setCurrentData({ labels: [], datasets: [] } as ChartData);
                return;
            }

            /// Real API Call

            const headers = {
                Authorization: `Bearer ${getCookieJsonField(
                    "DepSessionIdentity",
                    "token"
                )}`,
            };

            const params = {
                corridor_name: originalSelectedRoadway,
                direction: originalSelectedDirection,
                esu_corridor_name: originalSelectedCorridor,
                corridor_segment: originalSelectedCorridorSegment,
                "mile_marker-range": originalMileMarker.join(","),
                interval_min: originalSelectedDataRollupPeriod,
                display_choice: originalSelectedDataType?.join(","),
            };

            const primaryDataset: PlotItem[] = [];
            const latestDataset: PlotItem[] = [];
            const endpointList = ["current-day", "latest-point"];
            const promises = endpointList.map(endpoint => axios.get<EsuDataResponse>(`${props.apiBaseUrl}/${endpoint}`, { params, headers }));
            const responses = await Promise.all(promises);
            const data = responses.map(response => response.data);
            for (const item of data) {
                if (item.Primary != null) {
                    primaryDataset.push(...item.Primary);
                }
                if (item.Latest != null) {
                    latestDataset.push(...item.Latest);
                }
            }

            //const updatedChartData = { ...currentData };
            const labels: string[] = originalSelectedDataRollupPeriod === "15" ? generateTimeRangeDay(15) : generateTimeRangeDay(60);

            const updatedCurrentData = [];

            const lastUpdatedDate = new Date();
            let formattedUpdatedDate = lastUpdatedDate.toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true  // Specify whether to use 12-hour clock or 24-hour clock
            });
            if (selectedDataType?.includes("speed")) {
                const label = "Current Speed"
                const speedDataset: Dataset = {
                    label: label,
                    data: Array(labels.length).fill(null),
                    type: "line",
                    backgroundColor: getColorByLabel(label),
                    borderColor: getColorByLabel(label),
                    fill: false,
                    yAxisID: 'y',
                    xAxisID: 'x1',
                    borderWidth: 1,
                    order: 1,
                    spanGaps: true,
                    barThickness: 8
                };
                primaryDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Speed){
                            speedDataset.data[insertionIndex] = item.Speed;
                        }
                    });
                updatedCurrentData.push(speedDataset);
            }
            if (selectedDataType?.includes("volume")) {
                const label = "Current Volume"
                const volumeDataset: Dataset = {
                    label: "Current Volume",
                    data: Array(labels.length).fill(null),
                    type: volumePlotType,
                    backgroundColor: getColorByLabel(label),
                    borderColor: getColorByLabel(label),
                    fill: false,
                    yAxisID: 'y1',
                    xAxisID: 'x1',
                    borderWidth: 1,
                    order: 1,
                    spanGaps: true,
                    barThickness: originalSelectedDataRollupPeriod === "15" ? 4 : 16
                };
                primaryDataset.forEach((item : PlotItem) => {
                        const insertionIndex = labels.findIndex(data => data === timeStringToDateTimeObject(item.Timestamp));
                        if (item.Volume){
                            volumeDataset.data[insertionIndex] = item.Volume;
                        }
                    });
                updatedCurrentData.push(volumeDataset);
            }

            const updatedLatestData = [];
            const latestDateTime = timeStringToDate(latestDataset[latestDataset.length - 1].Timestamp);
            const _latestDataYear = latestDateTime.getFullYear();
            const _latestDataMonth = String(latestDateTime.getMonth() + 1).padStart(2, '0');
            const _latestDataDay = String(latestDateTime.getDate()).padStart(2, '0');
            const latestTime = timeStringToDateTimeObject(latestDataset[latestDataset.length - 1].Timestamp);
            formattedUpdatedDate = latestTime;
            setLastUpdated(formattedUpdatedDate)
            setLatestDataYear(_latestDataYear);
            setLatestDataMonth(_latestDataMonth)
            setLatestDataDay(_latestDataDay);
            if (_latestDataYear == todayYear &&
                _latestDataMonth == todayMonth &&
                _latestDataDay == todayDay){

                let latestTimeIndex = labels.findIndex(label => label === latestTime);

                // If the latestTime is not found, find the closest index to insert
                if (latestTimeIndex === -1) {
                    // Convert all times into Date objects for accurate comparison
                    const latestTimeDate = timeStringToDate(latestTime);
                    const labelDates = labels.map(time => timeStringToDate(time));
                    for (let i = 0; i < labelDates.length; i++) {
                        if (labelDates[i] > latestTimeDate) {
                            latestTimeIndex = i;
                            break;
                        }
                    }
                    if (latestTimeIndex === -1) latestTimeIndex = labels.length; // Append to end if no larger value found

                    // Insert the latest time at the correct index
                    labels.splice(latestTimeIndex, 0, latestTime);

                    // Ensure the labels are sorted after insertion
                    labels.sort((a, b) => (new Date(`1970-01-01T${a}`).getTime() - new Date(`1970-01-01T${b}`).getTime()));
                }
                if (selectedDataType?.includes("speed")) {
                    const speedDataset: Dataset = {
                        label: "Latest Speed",
                        data: Array(labels.length).fill(null),
                        type: "line",
                        borderColor: "white",
                        backgroundColor: "darkblue",
                        fill: false,
                        yAxisID: 'y',
                        xAxisID: 'x1',
                        pointRadius: 5, // Bigger point
                        pointHoverRadius: 6, // Bigger hover point
                        spanGaps: true,
                        barThickness: 8
                    };
                    speedDataset.data[latestTimeIndex] = latestDataset[0].Speed;
                    updatedLatestData.push(speedDataset);
                }

                if (selectedDataType?.includes("volume")) {
                    const volumeDataset: Dataset = {
                        label: "Latest Volume",
                        data: Array(labels.length).fill(null),
                        type: volumePlotType,
                        backgroundColor: "lightpink",
                        borderColor: "white",
                        fill: false,
                        yAxisID: 'y1',
                        xAxisID: 'x2',
                        pointRadius: 5, // Bigger point
                        pointHoverRadius: 6, // Bigger hover point
                        spanGaps: true,
                        barThickness: originalSelectedDataRollupPeriod === "15" ? 2 : 8
                    };
                    volumeDataset.data[latestTimeIndex] = latestDataset[0].Volume;
                    updatedLatestData.push(volumeDataset);
                }
            }

            const combinedDatasets = [...updatedCurrentData, ...updatedLatestData];

            console.debug(combinedDatasets);
            setCurrentData({labels, datasets: combinedDatasets});
            toast.current?.show({
                severity: "info",
                summary: "Data Updated",
                detail: "Data Updated",
                sticky: false,
            });
        } catch (error) {
            let errorMessage = 'An unknown error occurred';
            console.error("Error fetching latest data", error);
            if (error instanceof Error) {
                errorMessage = error.message || error.toString();
            } else if (axios.isAxiosError(error)) {
                // Axios error handling
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    errorMessage = error.response.data.message || error.response.statusText;
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'No response received from the server.';
                } else {
                    // Something happened in setting up the request that triggered an Error
                    errorMessage = error.message;
                }
            }

            toast.current?.show({
                severity: "error",
                summary: "Error fetching latest plot data",
                detail: errorMessage,
                sticky: true, // Show until dismissed by user
            });
        }
    };

    const startPolling = useCallback(() => {
        if (canPlot) {
            pollingInterval.current = setInterval(fetchLatestData, POLLING_INTERVAL * 60 * 1000); // 5 minutes
            if (moment().tz('America/New_York').isAfter(currentDay, 'day')) // We have transitioned to a new day
            {
                fetchPlotData();
                setCurrentDay(moment().tz('America/New_York'));
            }
            setIsPolling(true);
        }
    }, [fetchLatestData]);

    const stopPolling = useCallback(() => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
        }
        setIsPolling(false);
    }, []);

    useEffect(() => {
        if (isPolling) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => {
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
            }
        };
    }, [isPolling, startPolling, stopPolling]);


    useEffect(() => {
        if (originalPrimaryHistoricalStartDate != null && originalPrimaryHistoricalEndDate != null) {
            const currentDate = new Date(originalPrimaryHistoricalStartDate.format('L'))
            const endDate = new Date(originalPrimaryHistoricalEndDate.format('L'))
            const daysOfWeekSet = new Set<string>();

            while (currentDate <= endDate) {
                daysOfWeekSet.add(daysOfWeek[currentDate.getDay()]);
                currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
            }

            setSelectedDaysOfWeek(Array.from(daysOfWeekSet));
        }
        else {
            setSelectedDaysOfWeek([])
        }

    }, [originalPrimaryHistoricalStartDate, originalPrimaryHistoricalEndDate]);

    // interval in minutes
    function generateTimeRangeDay(interval : number) : string[]
    {
        const returnArray : string[] = [];
        const startTime : Date = new Date(2000,1,1,0,0,0);
        const endTime : Date = new Date(2000,1,2,0,0,0);

        while (startTime < endTime)
        {
            returnArray.push(startTime.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit'}));
            startTime.setMinutes(startTime.getMinutes() + interval);
        }

        return returnArray;
    }

    const isApplyButtonEnabled = () => {
        const isDataTypeSelected = (selectedDataType !== null && selectedDataType.length > 0);
        const isHistoricalDataOptionsSelected = (selectedHistoricalDataOptions !== null && selectedHistoricalDataOptions.length > 0);
        const isDataRollupPeriodSelected = selectedDataRollupPeriod !== null;
        const isRoadwaySelected = selectedRoadway !== null;

        let isAllRequiredSelected = isDataTypeSelected && isHistoricalDataOptionsSelected && isDataRollupPeriodSelected && isRoadwaySelected;
        if (selectedPlot.value === 2)
        {
            const isPrimaryHistoricalStartDateSelected = primaryHistoricalStartDate !== null;
            const isPrimaryHistoricalEndDateSelected = primaryHistoricalEndDate !== null;
            isAllRequiredSelected = isAllRequiredSelected && isPrimaryHistoricalStartDateSelected && isPrimaryHistoricalEndDateSelected;
        }

        const hasDataTypeSelectedChanged = (selectedDataType !== originalSelectedDataType);
        const hasHistoricalDataOptionsChanged = (selectedHistoricalDataOptions !== originalSelectedHistoricalDataOptions);
        const hasDataRollupPeriodChanged = (selectedDataRollupPeriod !== originalSelectedDataRollupPeriod);
        const hasRoadwaySelectedChanged = (selectedRoadway !== originalSelectedRoadway);

        let hasRequiredValuesChanged = hasDataTypeSelectedChanged || hasHistoricalDataOptionsChanged || hasDataRollupPeriodChanged || hasRoadwaySelectedChanged;
        if (selectedPlot.value === 2)
        {
            const hasPrimaryHistoricalStartDateChanged = primaryHistoricalStartDate !== originalPrimaryHistoricalStartDate;
            const hasPrimaryHistoricalEndDateChanged = primaryHistoricalEndDate !== originalPrimaryHistoricalEndDate;
            hasRequiredValuesChanged = hasRequiredValuesChanged || hasPrimaryHistoricalStartDateChanged || hasPrimaryHistoricalEndDateChanged;
        }

        const hasDirectionChanged = selectedDirection !== originalSelectedDirection;
        const hasEsuCorridorChanged = selectedCorridor !== originalSelectedCorridor;
        const hasCorridorSegmentChanged = selectedCorridorSegment !== originalSelectedCorridorSegment;
        const hasMileMarkerChanged = (mileMarker[0] !== originalMileMarker[0] || mileMarker[1] !== originalMileMarker[1]);

        const hasNonRequiredValuesChanged = hasDirectionChanged || hasEsuCorridorChanged || hasCorridorSegmentChanged || hasMileMarkerChanged;

        return isAllRequiredSelected && (hasRequiredValuesChanged || hasNonRequiredValuesChanged);
    }

    const isClearButtonEnabled = () => {

        const isDataTypeSelected = (selectedDataType !== null && selectedDataType.length > 0);
        const isHistoricalDataOptionsSelected = (selectedHistoricalDataOptions !== null && selectedHistoricalDataOptions.length > 0);
        const isDataRollupPeriodSelected = selectedDataRollupPeriod !== null;
        const isRoadwaySelected = selectedRoadway !== null;
        const isDirectionSelected = selectedDirection !== null;
        const isEsuCorridorSelected = selectedCorridor !== null;
        const isCorridorSegmentSelected = selectedCorridorSegment !== null;
        const isMileMarkerSelected = (mileMarker[0] !== mileMarkerRange[0] || mileMarker[1] !== mileMarkerRange[1]);

        const isAnythingSelected = isDataTypeSelected || isHistoricalDataOptionsSelected || isDataRollupPeriodSelected || isRoadwaySelected || isDirectionSelected || isEsuCorridorSelected || isCorridorSegmentSelected || isMileMarkerSelected;

        return isAnythingSelected;
    }

    const captureScreenshot = async () => {
        const node = document.getElementById("esu-report-element");
        let title = buildTitle().split(' ').join('_').toLowerCase();

        if (selectedPlot.value === 1){
            title += `_${todayYear}-${todayMonth}-${todayDay}`
        }

        htmlToImage
            .toPng(node as HTMLElement)
            .then(function (dataUrl) {
                const link = document.createElement('a');
                link.download = `${title}.png`;
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.error('oops, something went wrong!', err);
            });
    };

    const buildTitle = () => {
        const dataOptionsTitles = originalSelectedHistoricalDataOptions?.flatMap(
            (v) => historicalDataOptions.find((pt) => pt.value === v)?.label
        ).join(" and ");

        const dataTypeTitles = originalSelectedDataType?.flatMap(
            (v) => dataTypes.find((pt) => pt.value === v)?.label
        ).join(" and ");

        const rollupPeriod = originalSelectedDataRollupPeriod != null ? `${originalSelectedDataRollupPeriod}-minute` : '';

        if (selectedPlot.value === 1) {
            return `${originalSelectedCorridorSegment ?? originalSelectedCorridor ?? originalSelectedRoadway ?? ""} ${originalSelectedCorridorSegment ? '' : originalSelectedDirection ?? ""} ${originalSelectedHistoricalDataOptions != null ? 'Current vs' : ''} ${dataOptionsTitles ?? ""} ${rollupPeriod ?? ""} ${dataTypeTitles ?? ""}`;
        } else {
            const startDate = originalPrimaryHistoricalStartDate != null ? `${originalPrimaryHistoricalStartDate.format('L')} -` : '';
            const endDate = originalPrimaryHistoricalEndDate != null ? `${originalPrimaryHistoricalEndDate.format('L')} vs` : '';
            return `${originalSelectedCorridorSegment ?? originalSelectedCorridor ?? originalSelectedRoadway ?? ""} ${originalSelectedCorridorSegment ? '' : originalSelectedDirection ?? ""} ${startDate} ${endDate} ${dataOptionsTitles ?? ""} ${rollupPeriod ?? ""} ${dataTypeTitles ?? ""}`;
        }
    };

    // Map with collapsible user input tab
    return (
        <div className={styles.EsuReport} data-testid="EsuReport">
            <Toast ref={toast} />
            <div className="flex flex-column gap-2">
                <Panel header="Filters" toggleable
                    collapseIcon="pi pi-angle-up"
                    expandIcon="pi pi-angle-down"
                >
                    <div className={styles.filters}>
                        <div className={styles.filterItem}>
                            <label htmlFor="data-type-selector" data-pr-tooltip="This field is required">Data Types*</label>
                            <MultiSelect
                                id="data-type-selector"
                                value={selectedDataType}
                                onChange={(e) => {
                                    setSelectedDataType(e.value);
                                    setSelectedDataTypeAll(
                                        e.value.length === dataTypes?.length
                                    );
                                }}
                                options={dataTypes.sort((a, b) => a.label.localeCompare(b.label))}
                                selectAll={selectedDataTypeAll}
                                placeholder="Data Type Selector"
                                tooltip="Select data types. This is a required field."
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <label htmlFor="historical-data-options" data-pr-tooltip="This field is required">Historical Data Options*</label>
                            <MultiSelect
                                id="historical-data-options"
                                value={selectedHistoricalDataOptions}
                                onChange={(e) => {
                                    setSelectedHistoricalDataOptions(e.value);
                                    setSelectedHistoricalDataOptionsAll(
                                        e.value.length === historicalDataOptions?.length
                                    );
                                }}
                                options={historicalDataOptions.sort((a, b) => a.label.localeCompare(b.label))}
                                selectAll={selectedHistoricalDataOptionsAll}
                                placeholder="Historical Data Options"
                                maxSelectedLabels={1}
                                tooltip="Select historical data options. This is a required field."
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <label htmlFor="data-rollup-period" data-pr-tooltip="This field is required">Data Rollup Period*</label>
                            {dataRollupPeriodOptions.map((category) => (
                                <div key={category.value} className="flex align-items-center">
                                    <RadioButton inputId={category.value} name="category" value={category.value} onChange={(e) => setSelectedDataRollupPeriod(e.value)}
                                        checked={selectedDataRollupPeriod === category.value} tooltip="This field is required" disabled={isLoading}/>
                                    <label htmlFor={category.value} className="ml-2">{category.label}</label>
                                </div>
                            ))}
                        </div>

                        {selectedPlot.value === 2 && (
                            <div className={styles.filterItem}>
                                <label htmlFor="primary-historical-start" data-pr-tooltip="This field is required">Primary Historical Start Date*</label>
                                <Calendar
                                    id="primary-historical-start"
                                    value={primaryHistoricalStartDate === null ? null : new Date(primaryHistoricalStartDate!.format('L'))}
                                    onChange={(e: CalendarChangeParams) => {
                                        const startDate = moment(e.value as string);
                                        const startDateEt = startDate.tz('America/New_York');
                                        const midnightStartDateEt = startDateEt.hours(0);
                                        setPrimaryHistoricalStartDate(midnightStartDateEt);
                                        checkCanPlot();
                                    }}
                                    showIcon
                                    placeholder="Select Start Date"
                                    tooltip="Select the primary historical start date. This is a required field."
                                    minDate={MIN_DATE.toDate()}
                                    maxDate={primaryHistoricalEndDate?.toDate() ?? MAX_DATE.toDate()}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        {selectedPlot.value === 2 && (
                            <div className={styles.filterItem}>
                                <label htmlFor="primary-historical-end" data-pr-tooltip="This field is required">Primary Historical End Date*</label>
                                <Calendar
                                    id="primary-historical-end"
                                    value={primaryHistoricalEndDate === null ? null : new Date(primaryHistoricalEndDate!.format('L'))}
                                    onChange={(e: CalendarChangeParams) => {
                                        const endDate = moment(e.value as string);
                                        const endDateEt = endDate.tz('America/New_York');
                                        const midnightEndDateEt = endDateEt.hours(0);
                                        const endDateEtEndofDay = midnightEndDateEt.add({
                                            hours: 23,
                                            minutes: 59,
                                            seconds: 59
                                        });
                                        setPrimaryHistoricalEndDate(endDateEtEndofDay);
                                        checkCanPlot();
                                    }}
                                    showIcon
                                    placeholder="Select End Date"
                                    tooltip="Select the primary historical end date. This is a required field."
                                    minDate={primaryHistoricalStartDate == null ? undefined : new Date(primaryHistoricalStartDate.format('L'))}
                                    maxDate={MAX_DATE.toDate()}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        <div className={styles.filterItem}>
                            <label htmlFor="roadway" data-pr-tooltip="This field is required">Roadway*</label>
                            <Dropdown
                                id="roadway"
                                value={selectedRoadway}
                                onChange={(e) => setSelectedRoadway(e.value)}
                                options={roadwayOptions?.sort()}
                                placeholder="Roadway"
                                filter
                                tooltip="Select a roadway. This is a required field."
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <label htmlFor="direction">Direction</label>
                            <Dropdown
                                id="direction"
                                value={selectedDirection}
                                onChange={(e) => setSelectedDirection(e.value)}
                                options={directionOptions?.sort()}
                                placeholder="Direction"
                                tooltip="Select a direction."
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <label htmlFor="corridor">ESU Corridor</label>
                            <Dropdown
                                id="corridor"
                                value={selectedCorridor}
                                onChange={(e) => setSelectedCorridor(e.value)}
                                options={corridorOptions?.sort()}
                                placeholder="Corridor"
                                filter
                                tooltip="Select a Corridor: (from beginning to ending mile marker)"
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <label htmlFor="corridor-segments">Corridor Segment</label>
                            <Dropdown
                                id="corridor-segments"
                                value={selectedCorridorSegment}
                                onChange={(e) => setSelectedCorridorSegment(e.value)}
                                options={corridorSegmentOptions?.sort()}
                                placeholder="Corridor Segment"
                                filter
                                tooltip="Select a corridor segment."
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <div className="flex flex-column gap-2">
                                <label htmlFor="mile-marker">Mile Marker Range</label>
                                <div className="flex align-items-center" style={{ width: '100%' }}>
                                    <Slider
                                        style={{ width: '100%' }}
                                        value={mileMarker}
                                        onChange={(e: SliderChangeParams) => setMileMarker(e.value as [number, number])}
                                        range
                                        step={1}
                                        min={mileMarkerRange[0]}
                                        max={mileMarkerRange[1]}
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="flex justify-content-between">
                                    <div className="flex flex-column">
                                        <InputNumber
                                            value={mileMarker[0]}
                                            onChange={(e) => setMileMarker([e.value ?? 0 as number, mileMarker[1]])} // If someone inputs a blank into the milemarker field it comes through here as null.
                                            min={mileMarkerRange[0]}
                                            max={mileMarkerRange[1]}
                                            step={1}
                                            maxLength={4}
                                            size={4}
                                            tooltip="Minimum"
                                            inputId="min-milemarker-input"
                                            disabled={isLoading}
                                        />
                                        <label htmlFor="min-milemarker-input" className="font-bold block mb-2">Min</label>
                                    </div>
                                    <div className="flex flex-column">
                                        <InputNumber
                                            value={mileMarker[1]}
                                            onChange={(e) => setMileMarker([mileMarker[0], e.value ?? 0 as number])}
                                            min={mileMarkerRange[0]}
                                            max={mileMarkerRange[1]}
                                            step={1}
                                            maxLength={4}
                                            size={4}
                                            tooltip="Maximum"
                                            inputId="max-milemarker-input"
                                            disabled={isLoading}
                                        />
                                        <label htmlFor="max-milemarker-input" className="font-bold block mb-2">Max</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row align-items-center justify-content-center">
                        <div className={styles.filtersButtons}>
                            <div className={styles.filterItem}>
                                <Button
                                    aria-controls="clear filters"
                                    title="Clear Filters"
                                    label="Clear Filters"
                                    tooltip="Clear and reset filters"
                                    className="p-button p-button-text"
                                    onClick={() => clearFilters()}
                                    disabled={isLoading || !isClearButtonEnabled()}
                                />
                            </div>
                            <div className={styles.filterItem}>
                                <Button
                                    aria-controls="apply-filters"
                                    title="Apply Filters"
                                    label="Apply Filters"
                                    tooltip="Apply pending filters and plot data"
                                    className="p-button p-button-text"
                                    onClick={() => applyFilters()}
                                    disabled={isLoading || !isApplyButtonEnabled()}
                                />
                            </div>
                            <div className={styles.filterItem}>
                                <Button
                                    aria-controls="revert-filters"
                                    title="Revert Changes"
                                    label="Revert Changes"
                                    tooltip="Revert changes to filters to current plot state"
                                    className="p-button p-button-text"
                                    onClick={() => revertFilters()}
                                    disabled={isLoading || !hasPendingChanges()}
                                />
                            </div>
                            <div className={styles.filterItem}>
                                {selectedPlot.value === 1 && (
                                    <Button
                                        aria-controls="plot"
                                        title="Refresh"
                                        label="Refresh"
                                        tooltip="Manually refresh plot"
                                        className="p-button p-button-text"
                                        onClick={() => executePlot()}
                                        disabled={isLoading || !canPlot || !isClearButtonEnabled()}
                                    />
                                )}
                            </div>
                            <div className={styles.filterItem}>
                                {selectedPlot.value === 1 && (
                                    <Button
                                        aria-controls="toggle-autorefresh"
                                        title={isPolling ? "Stop Auto-refresh" : "Start Auto-refresh"}
                                        label={isPolling ? "Stop Auto-refresh" : "Start Auto-refresh"}
                                        tooltip={isPolling ? "Stop automatically refreshing data every 5 minutes" : "Start automatically refreshing data every 5 minutes"}
                                        className="p-button p-button-text"
                                        onClick={() => {
                                            setPollCount(0)
                                            setIsPolling((prev) => !prev)
                                        }}
                                        disabled={isLoading || !canPlot || !isClearButtonEnabled()}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </Panel>
                <div className="flex flex-row align-items-center justify-content-start gap-4" key={esuUpdateKey}>
                    <div className={styles.esuReportElement + " flex flex-column w-11 gap-4"} id={"esu-report-element"}>
                        <div className="flex flex-row align-items-center justify-content-center" style={{ textAlign: 'center' }}>
                            <h3>
                                {buildTitle()}
                            </h3>
                        </div>
                        {isLoading && (
                            <>
                                <ProgressSpinner />
                            </>
                        )}
                        {!isLoading && (
                            <>
                                <Chart type="line" data={chartData} options={chartOptions}/>
                            </>
                        )}

                        <div className="flex flex-row gap-4 align-items-start ">
                            <div className="flex flex-column w-12">
                                {selectedPlot.value === 1 && (
                                    <>
                                        <p style={{ alignItems: "center", }}>Last Updated: {latestDataYear ?? ""}-{latestDataMonth}-{latestDataDay} {lastUpdated !== "" ? lastUpdated + " ET" : ""} </p>
                                        <p>Historical Day of Week: {dayOfWeek}</p>
                                    </>
                                )}
                                {selectedPlot.value === 2 && (
                                    <>
                                        <p>Historical Days of Week: {selectedDaysOfWeek.join(", ")}</p>
                                    </>
                                )}

                            </div>
                        </div>
                    </div>
                    <div className="flex flex-column w-2 align-items-start justify-content-center gap-4" style={{ alignItems: "start" }}>
                        <label htmlFor="volume_plot_type">Volume Plot Type</label>
                        <SelectButton value={volumePlotType}
                            id="volume_plot_type"
                            onChange={(e) => {
                                setVolumePlotType(e.value);
                            }}
                            options={["line", "bar"]}
                            className="custom-select-button"
                        />
                        <Button
                            aria-controls="capture-plot-image"
                            title="Download Chart"
                            label="Download Chart"
                            tooltip="Download chart as PNG image"
                            className="p-button p-button-text"
                            onClick={() => {
                                captureScreenshot()
                            }}
                        />
                    </div>

                </div>
                <div className="flex flex-row align-items-center">
                    <div className="flex flex-column w-4">
                        <SelectButton value={selectedPlotButton}
                            onChange={async (e) => {
                                const plot = plotTypes.find((pt) => pt.value === e.value);
                                if (plot) {
                                    setSelectedPlot(plot);
                                    setSelectedPlotButton(e.value);
                                    await clearFilters();
                                }
                            }}
                            options={plotTypes}
                            optionLabel="label"
                            className="custom-select-button"
                            disabled={isLoading} // disable while plot is plotting to remove potential errors switching between plots
                        />

                    </div>
                </div>
                <div className="flex flex-row align-items-center justify-content-end gap-2">
                    <div className="flex flex-column w-2">
                        <div style={{width: 'fit-content', height: 'fit-content'}}>
                            <a href={`${props.baseUrl}/assets/FDOT DEPOT ESU Traffic Analysis Report.pdf`} download={'FDOT DEPOT ESU Traffic Analysis Report.pdf'}>Download User Guide</a>
                        </div>
                    </div>
                    <div className="flex flex-column w-2">
                        <p>
                            Average Data 1-Year - The average historical data from all days of the same day of the week as the {selectedPlot.value === 1 ? "current day of the week" : "primary date range"} in the past year.
                        </p>
                    </div>
                    <div className="flex flex-column w-2">
                        <p>
                            Average Data 3-Months - The average historical data from all days of the same day of the week as the {selectedPlot.value === 1 ? "current day of the week" : "primary date range"} for the previous &quot;season&quot; which is the 3 calendar months preceding the {selectedPlot.value === 1 ? "current date" : "primary date range"}.
                        </p>
                    </div>
                    <div className="flex flex-column w-2">
                        <p>
                            Volume {originalSelectedDataRollupPeriod == "15" ? "(Vehicles/15m/Lane)" : "(Vehicles/60m/Lane)"} - Total vehicles detected for the period for a link divided by the number of lanes for the link and then averaged across all links.
                        </p>
                    </div>
                    <div className="flex flex-column w-2">
                        <p>
                            Speed (Mph) - Average speed across all lanes in miles per hours, averaged across all polls in the period, averaged across all links.
                        </p>
                    </div>
                </div>
            </div>
        </div>);
};


export default EsuReport;
